import { productClick, productImpression } from 'analytics/enhanced-ecommerce'
import { GAImpressionFieldObject, productClickGenericObject, productImpressionGenericObject } from 'analytics/eventDefinitions'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import StickyPromptCard from 'components/Common/StickyPromptCard/StickyPromptCard'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { useAppSelector } from 'hooks/reduxHooks'
import uuidV4 from 'lib/string/uuidV4Utils'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import PriceRowPriceWithCaption from 'components/Luxkit/PricePoints/PriceRowPriceWithCaption'
import StickyPromptCardFooter from 'components/Common/StickyPromptCard/StickyPromptCardFooter'
import StickyPromptCardContent, { StickyCardImageWidth } from 'components/Common/StickyPromptCard/StickyPromptCardContent'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import styled from 'styled-components'
import { rem } from 'polished'

// make sure the button is vertically aligned with the image
const BookNowButton = styled(TextButton)`
  width: ${rem(StickyCardImageWidth)};
`

export interface StickyCardViewModel{
  show: boolean,
  offerDisplayName: string,
  durationText: string,
  checkInOutText?: string,
  occupancyText?: string,
  image: App.Image,
  price: number,
  discountPercent: number,
  offerId: string,
  offerAnalyticsType: string,
  analyticsListName: string,
  analyticsCardType: string,
  cardHeading: string,
  toUrl: string,
  onClick: () => void,
  onClose: () => void,
  stickyTop?: number,
}

interface Props extends StickyCardViewModel {
}

function ParameterizedStickyCard({
  show,
  offerDisplayName,
  durationText,
  checkInOutText,
  occupancyText,
  image,
  price,
  discountPercent,
  offerId,
  offerAnalyticsType,
  analyticsListName,
  analyticsCardType,
  cardHeading,
  toUrl,
  onClick,
  onClose,
  stickyTop,
}: Props) {
  // analytics
  const currentCurrency = useAppSelector((state) => { return state.geo.currentCurrency })
  const listId = useMemo(() => uuidV4(), [])

  const [inViewRef, inView] = useInView({ triggerOnce: true })

  const onImpression = useCallback(() => {
    const impression: GAImpressionFieldObject = productImpressionGenericObject({
      id: offerId,
      name: offerDisplayName,
      category: offerAnalyticsType,
      price,
      position: 1,
      listName: analyticsListName,
    })
    productImpression(currentCurrency, [impression], {
      listId,
      listName: analyticsListName,
    })
  }, [listId, offerId, offerDisplayName, offerAnalyticsType, currentCurrency, price, analyticsListName])

  const onClickThrough = useCallback(() => {
    const clickObject = productClickGenericObject(
      {
        id: offerId,
        name: offerDisplayName,
        category: offerAnalyticsType,
        price,
        position: 1,
      },
    )
    productClick(currentCurrency, clickObject, {
      listId,
      listName: analyticsListName,
    })
  }, [listId, offerId, offerDisplayName, offerAnalyticsType, currentCurrency, price, analyticsListName])

  // interaction handlers
  const handleDismiss = useCallback(() => {
    onClose()
    Analytics.track('client-event', clientEvent(listId, `${analyticsCardType}_dismissed`, 'lere', 'interaction'))
  }, [listId, analyticsCardType, onClose])

  const handleClick = useCallback(() => {
    onClick()
    onClickThrough()
  }, [onClick, onClickThrough])

  useEffect(() => {
    if (show && inView) {
      onImpression()
    }
  }, [show, onImpression, inView])

  const caption = useMemo(() => {
    return [checkInOutText, occupancyText].filter(Boolean).join(', ')
  }, [checkInOutText, occupancyText])

  return (
    <StickyPromptCard
      title={cardHeading}
      to={toUrl}
      show={show}
      onClose={handleDismiss}
      onClick={handleClick}
      stickyTop={stickyTop}
      ref={inViewRef}
    >
      <StickyPromptCardContent image={image}>
        <Group direction="vertical" gap={4}>
          <BodyText weight="bold" variant="medium" colour="neutral-two" lineClamp={1}>
            {offerDisplayName}
          </BodyText>
          {!!caption && <Caption variant="large">{caption}</Caption>}
        </Group>
      </StickyPromptCardContent>
      <StickyPromptCardFooter
        callToAction={<BookNowButton kind="primary" size="small">Book now</BookNowButton>}
      >
        <VerticalSpacer gap={4}>
          <PriceRowPriceWithCaption
            caption={durationText}
            price={price}
            saleUnit="total"
            size="S"
            taxesAndFees="exclusive"
          />
          {discountPercent > MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={discountPercent}
          />}
        </VerticalSpacer>
      </StickyPromptCardFooter>
    </StickyPromptCard>
  )
}

export default ParameterizedStickyCard
